import { useEffect } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { Button, isTouchDevice, Modal, TextField } from "@shapr3d/frontend-lib";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { z } from "zod";

import { useUpdateTeamSpaceName } from "../../hooks/useTeamSpaces";
import { useTracker } from "../../hooks/useTracker";
import useTranslation from "../../hooks/useTranslation";
import { ElementClickAction, ElementClickLocation } from "../../lib/TrackingAdapter";

import style from "./SpaceNameChangeModal.module.css";

const schema = z.object({
  name: z.string().nonempty("Please enter a space name"),
});

type SpaceNameChangeFormData = z.infer<typeof schema>;

export default function SpaceNameChangeModal({
  open = true,
  onClose,
  name,
  spaceID,
}: {
  open?: boolean;
  onClose: () => void;
  name?: string;
  spaceID: string;
}) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<SpaceNameChangeFormData>({
    resolver: zodResolver(schema),
    defaultValues: { name },
  });

  useEffect(() => {
    if (open) {
      reset({ name });
    }
  }, [open, name, reset]);

  const tracker = useTracker();

  const { teamID } = useParams();
  const { t, m } = useTranslation();
  const { mutateAsync, error } = useUpdateTeamSpaceName(teamID);

  const submit = async (data: SpaceNameChangeFormData) => {
    if (!teamID) {
      return;
    }
    try {
      await mutateAsync({ teamID, spaceID, name: data.name });
      onClose();
      tracker.trackButtonClick({
        action: ElementClickAction.CONFIRM,
        location: ElementClickLocation.SPACE_RENAME_MODAL,
      });
    } catch (error) {
      // This is to not throw Unhandled Promise Rejection error
    }
  };

  let errorMessage = null;
  if (error) {
    errorMessage = t(m.ErrorMessage.Unknown);
  }

  return (
    <Modal open={open} title="Rename space" size="medium" onClose={onClose}>
      <form className={style.teamNameChangeForm} onSubmit={handleSubmit(submit)}>
        <TextField
          {...register("name")}
          placeholder="e.g. John's Work, Design Team, Modeling..."
          errorMessage={errors.name?.message}
          maxLength={200}
          disabled={isSubmitting}
          autoFocus={!isTouchDevice()}
        ></TextField>

        {errorMessage && <div className={style.error}>{errorMessage}</div>}
        <div className={style.buttonContainer}>
          <Button type="submit" disabled={isSubmitting}>
            Save
          </Button>
        </div>
      </form>
    </Modal>
  );
}
